import { useEffect, useRef } from "react";
import { Calendar } from "lucide-react";

/**
 * Embeds OpenTable's standard reservation widget for a given restaurant ID.
 * The OpenTable loader script appends an iframe right after itself, so we mount
 * the script inside a host <div> and let OpenTable render into it.
 */
export default function OpenTableWidget({ rid, businessName }) {
  const hostRef = useRef(null);

  useEffect(() => {
    const cleanRid = String(rid || "").replace(/\D/g, "");
    if (!cleanRid || !hostRef.current) return;
    const host = hostRef.current;
    host.replaceChildren(); // clear previous render on rid change

    const params = new URLSearchParams({
      rid: cleanRid,
      type: "standard",
      theme: "standard",
      overlay: "false",
      domain: "com",
      lang: "en-US",
      newtab: "true",
      ot_source: "Servrevu",
    });

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.src = `https://www.opentable.com/widget/reservation/loader?${params.toString()}`;
    host.appendChild(script);

    return () => {
      host.replaceChildren();
    };
  }, [rid]);

  const cleanRid = String(rid || "").replace(/\D/g, "");
  if (!cleanRid) return null;

  return (
    <div className="mt-8" data-testid="opentable-widget-block">
      <h3 className="overline text-gray-500 inline-flex items-center gap-1.5">
        <Calendar size={12} /> Reserve a table
      </h3>
      <p className="text-sm text-gray-600 mt-2">
        Book a table at {businessName || "this restaurant"} through OpenTable — no account needed.
      </p>
      <div
        className="mt-4 rounded-md border border-border bg-white p-4 inline-block min-h-[180px]"
        data-testid="opentable-widget-host"
        ref={hostRef}
      />
      <p className="text-[11px] font-mono text-gray-400 mt-2">
        Powered by OpenTable. Reservations are subject to OpenTable's terms.
      </p>
    </div>
  );
}
